(() => {
  const ROOT_SELECTOR = '[data-hkwtt-root]';
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  const getYouTubeId = (url = '') => {
    const patterns = [
      /youtu\.be\/([^?&#/]+)/i,
      /youtube\.com\/watch\?(?:.*&)?v=([^?&#/]+)/i,
      /youtube\.com\/shorts\/([^?&#/]+)/i,
      /youtube\.com\/embed\/([^?&#/]+)/i
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match?.[1]) return match[1];
    }
    return '';
  };

  const getVimeoId = (url = '') => {
    const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    return match?.[1] || '';
  };

  const isNativeVideo = (url = '') =>
    /\.(mp4|webm|ogg)(?:[?#].*)?$/i.test(url);

  const loadScriptOnce = (src, readyCheck) =>
    new Promise((resolve, reject) => {
      if (readyCheck?.()) {
        resolve();
        return;
      }

      const existing = Array.from(document.scripts).find((script) => script.src === src);
      if (existing) {
        const wait = () => {
          if (readyCheck?.()) resolve();
          else window.setTimeout(wait, 40);
        };
        wait();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

  let youtubeApiPromise = null;
  const ensureYouTubeApi = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);

    if (!youtubeApiPromise) {
      youtubeApiPromise = new Promise((resolve, reject) => {
        const previousReady = window.onYouTubeIframeAPIReady;

        window.onYouTubeIframeAPIReady = () => {
          try {
            if (typeof previousReady === 'function') previousReady();
          } catch (_) {}
          resolve(window.YT);
        };

        loadScriptOnce(
          'https://www.youtube.com/iframe_api',
          () => Boolean(window.YT?.Player)
        )
          .then(() => {
            if (window.YT?.Player) resolve(window.YT);
          })
          .catch(reject);
      });
    }

    return youtubeApiPromise;
  };

  let vimeoApiPromise = null;
  const ensureVimeoApi = () => {
    if (window.Vimeo?.Player) return Promise.resolve(window.Vimeo);

    if (!vimeoApiPromise) {
      vimeoApiPromise = loadScriptOnce(
        'https://player.vimeo.com/api/player.js',
        () => Boolean(window.Vimeo?.Player)
      ).then(() => window.Vimeo);
    }

    return vimeoApiPromise;
  };

  const initRoot = (root) => {
    if (!root || root.dataset.hkwttInitialized === 'true') return;
    root.dataset.hkwttInitialized = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-hkwtt-tab]'));
    const reviews = Array.from(root.querySelectorAll('[data-hkwtt-review]'));
    const voices = Array.from(root.querySelectorAll('[data-hkwtt-voice]'));
    const stage = root.querySelector('[data-hkwtt-stage]');
    const poster = root.querySelector('[data-hkwtt-poster]');
    const empty = root.querySelector('[data-hkwtt-empty]');
    const quote = root.querySelector('[data-hkwtt-quote]');
    const quoteMeta = root.querySelector('[data-hkwtt-quote-meta]');
    const progress = root.querySelector('[data-hkwtt-progress]');
    const progressFill = root.querySelector('[data-hkwtt-progress-fill]');
    const tabsTrack = root.querySelector('.hkwtt__tabs');
    const reviewersTrack = root.querySelector('.hkwtt__reviewers');
    const voicesTrack = root.querySelector('.hkwtt__voices');

    if (!stage || !reviews.length) return;

    const revealInMobileTrack = (track, item, behavior = 'smooth') => {
      if (!track || !item || window.matchMedia('(min-width: 750px)').matches) return;
      requestAnimationFrame(() => {
        const target = item.offsetLeft - Math.max(0, (track.clientWidth - item.offsetWidth) / 2);
        track.scrollTo({ left: Math.max(0, target), behavior });
      });
    };

    const autoplayEnabled = root.dataset.autoplay !== 'false';
    const videoAutoplayEnabled = root.dataset.videoAutoplay !== 'false';
    const autoplaySeconds = clamp(Number(root.dataset.autoplaySeconds) || 7, 4, 15);
    const autoplayMs = autoplaySeconds * 1000;

    let activeCategory = root.dataset.defaultCategory || tabs[0]?.dataset.category || 'h2-pro';
    let activeReview = null;
    let player = null;
    let mediaController = null;
    let mediaPlaying = false;
    let mediaSession = 0;
    let raf = 0;
    let cycleStart = 0;
    let pauseStarted = 0;
    let pausedTotal = 0;
    let inView = true;
    let destroyed = false;

    const categoryReviews = () =>
      reviews.filter((review) => !review.hidden && review.dataset.category === activeCategory);

    const updateProgress = (value) => {
      const p = clamp(value, 0, 1);
      if (progressFill) progressFill.style.transform = `scaleX(${p})`;
      root.style.setProperty('--hkwtt-review-progress', p.toFixed(4));
    };

    const clearPlayer = () => {
      mediaSession += 1;
      mediaPlaying = false;

      if (mediaController) {
        try {
          if (typeof mediaController.destroy === 'function') mediaController.destroy();
          else if (typeof mediaController.unload === 'function') mediaController.unload();
        } catch (_) {}
        mediaController = null;
      }

      if (player) {
        if (player.tagName === 'VIDEO') {
          try { player.pause(); } catch (_) {}
        }
        player.remove();
        player = null;
      }

      stage.querySelectorAll('.hkwtt__player').forEach((node) => node.remove());
    };

    const setPoster = (src, alt = '') => {
      if (!poster) return;
      poster.classList.remove('is-hidden');
      if (src) {
        poster.src = src;
        poster.alt = alt;
        poster.hidden = false;
      } else {
        poster.removeAttribute('src');
        poster.alt = '';
        poster.hidden = true;
      }
    };

    const setEmpty = (show) => {
      if (empty) empty.hidden = !show;
    };

    const markReady = () => {
      stage.classList.remove('is-loading');
      poster?.classList.add('is-hidden');
      player?.classList.add('is-ready');
    };

    const loadMedia = (review) => {
      const url = (review.dataset.videoUrl || '').trim();
      const thumb = (review.dataset.thumbnail || '').trim();
      const title = review.dataset.title || review.dataset.channel || 'Review video';

      clearPlayer();
      mediaPlaying = false;
      root.classList.remove('is-video-playing');
      setEmpty(false);
      setPoster(thumb, title);

      if (!url) {
        stage.classList.remove('is-loading');
        if (!thumb) setEmpty(true);
        return;
      }

      stage.classList.add('is-loading');

      const yt = getYouTubeId(url);
      if (yt) {
        const iframe = document.createElement('iframe');
        iframe.className = 'hkwtt__player';
        iframe.title = title;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        iframe.loading = 'eager';
        iframe.src =
          `https://www.youtube-nocookie.com/embed/${encodeURIComponent(yt)}` +
          `?autoplay=${videoAutoplayEnabled ? '1' : '0'}` +
          `&mute=${videoAutoplayEnabled ? '1' : '0'}` +
          '&controls=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1';
        iframe.addEventListener('load', markReady, { once: true });
        stage.insertBefore(iframe, progress || null);
        player = iframe;

        const session = mediaSession;
        ensureYouTubeApi()
          .then(() => {
            if (session !== mediaSession || !iframe.isConnected || !window.YT?.Player) return;

            mediaController = new window.YT.Player(iframe, {
              events: {
                onStateChange: (event) => {
                  if (session !== mediaSession) return;

                  if (event.data === window.YT.PlayerState.PLAYING) {
                    setMediaPlaying(true);
                  } else if (
                    event.data === window.YT.PlayerState.PAUSED ||
                    event.data === window.YT.PlayerState.ENDED
                  ) {
                    setMediaPlaying(false, true);
                  }
                }
              }
            });
          })
          .catch(() => {});

        return;
      }

      const vimeo = getVimeoId(url);
      if (vimeo) {
        const iframe = document.createElement('iframe');
        iframe.className = 'hkwtt__player';
        iframe.title = title;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.loading = 'eager';
        iframe.src =
          `https://player.vimeo.com/video/${encodeURIComponent(vimeo)}` +
          `?autoplay=${videoAutoplayEnabled ? '1' : '0'}` +
          `&muted=${videoAutoplayEnabled ? '1' : '0'}` +
          '&title=0&byline=0&portrait=0&api=1';
        iframe.addEventListener('load', markReady, { once: true });
        stage.insertBefore(iframe, progress || null);
        player = iframe;

        const session = mediaSession;
        ensureVimeoApi()
          .then(() => {
            if (session !== mediaSession || !iframe.isConnected || !window.Vimeo?.Player) return;

            const controller = new window.Vimeo.Player(iframe);
            mediaController = controller;

            controller.on('play', () => {
              if (session === mediaSession) setMediaPlaying(true);
            });
            controller.on('pause', () => {
              if (session === mediaSession) setMediaPlaying(false, true);
            });
            controller.on('ended', () => {
              if (session === mediaSession) setMediaPlaying(false, true);
            });
          })
          .catch(() => {});

        return;
      }

      if (isNativeVideo(url)) {
        const video = document.createElement('video');
        video.className = 'hkwtt__player hkwtt__native-video';
        video.src = url;
        video.controls = true;
        video.autoplay = videoAutoplayEnabled;
        video.muted = videoAutoplayEnabled;
        video.playsInline = true;
        video.preload = 'metadata';
        video.addEventListener('loadeddata', markReady, { once: true });
        video.addEventListener('play', () => setMediaPlaying(true));
        video.addEventListener('pause', () => {
          if (!video.ended) setMediaPlaying(false, true);
        });
        video.addEventListener('ended', () => setMediaPlaying(false, true));
        stage.insertBefore(video, progress || null);
        player = video;
        if (videoAutoplayEnabled) {
          video.play().catch(() => {});
        }
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.className = 'hkwtt__player';
      iframe.title = title;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.loading = 'eager';
      iframe.src = url;
      iframe.addEventListener('load', markReady, { once: true });
      stage.insertBefore(iframe, progress || null);
      player = iframe;
    };

    const syncQuote = (review) => {
      if (quote) quote.textContent = review.dataset.quote ? `“${review.dataset.quote}”` : '';
      if (quoteMeta) {
        const channel = review.dataset.channel || '';
        quoteMeta.textContent = channel ? `${channel} · Independent review` : '';
      }
    };

    const syncVoices = (review) => {
      voices.forEach((voice) => {
        const active = voice.dataset.reviewId === review.dataset.reviewId;
        voice.classList.toggle('is-active', active);
        voice.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const restartCycle = () => {
      cancelAnimationFrame(raf);
      updateProgress(0);
      cycleStart = performance.now();
      pauseStarted = 0;
      pausedTotal = 0;

      if (autoplayEnabled && categoryReviews().length > 1) {
        root.classList.add('is-autoplaying');
        if (progress) progress.hidden = false;
        raf = requestAnimationFrame(tick);
      } else {
        root.classList.remove('is-autoplaying');
        if (progress) progress.hidden = true;
      }
    };

    const setMediaPlaying = (isPlaying, restartWhenPaused = false) => {
      if (mediaPlaying === isPlaying && !restartWhenPaused) return;

      mediaPlaying = isPlaying;
      root.classList.toggle('is-video-playing', mediaPlaying);

      if (mediaPlaying) {
        /*
          Visitor chose to watch the video:
          stop the auto-review countdown immediately and reset it.
          This guarantees the review can never switch while the video is playing.
        */
        cancelAnimationFrame(raf);
        updateProgress(0);
        cycleStart = performance.now();
        pauseStarted = 0;
        pausedTotal = 0;
        root.classList.remove('is-autoplaying');
        return;
      }

      if (restartWhenPaused) {
        /*
          Pause/end behaves like the idle state:
          start a fresh full countdown before moving to the next review.
        */
        restartCycle();
      }
    };

    const selectReview = (review, restart = true) => {
      if (!review) return;

      activeReview = review;

      reviews.forEach((item) => {
        const active = item === review;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.setAttribute('tabindex', active ? '0' : '-1');
      });

      syncVoices(review);
      syncQuote(review);
      loadMedia(review);

      revealInMobileTrack(reviewersTrack, review);
      const activeVoice = voices.find((voice) => voice.dataset.reviewId === review.dataset.reviewId && !voice.hidden);
      revealInMobileTrack(voicesTrack, activeVoice);

      if (restart) restartCycle();
    };

    const selectCategory = (category, pickFirst = true) => {
      activeCategory = category;

      tabs.forEach((tab) => {
        const active = tab.dataset.category === category;
        const hasReviews = reviews.some((review) => review.dataset.category === tab.dataset.category);
        tab.hidden = !hasReviews;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });

      reviews.forEach((review) => {
        review.hidden = review.dataset.category !== category;
      });

      voices.forEach((voice) => {
        voice.hidden = voice.dataset.category !== category;
      });

      const available = categoryReviews();
      const activeTab = tabs.find((tab) => tab.dataset.category === category && !tab.hidden);
      revealInMobileTrack(tabsTrack, activeTab);
      if (pickFirst && available.length) selectReview(available[0], true);
      return available;
    };

    const goNext = () => {
      const available = categoryReviews();
      if (available.length < 2) {
        restartCycle();
        return;
      }

      const currentIndex = Math.max(0, available.indexOf(activeReview));
      const next = available[(currentIndex + 1) % available.length];
      selectReview(next, true);
    };

    function tick(now) {
      if (destroyed || !autoplayEnabled) return;

      if (!inView || document.hidden || mediaPlaying) {
        if (!pauseStarted) pauseStarted = now;
        raf = requestAnimationFrame(tick);
        return;
      }

      if (pauseStarted) {
        pausedTotal += now - pauseStarted;
        pauseStarted = 0;
      }

      const elapsed = now - cycleStart - pausedTotal;
      const progressValue = clamp(elapsed / autoplayMs, 0, 1);
      updateProgress(progressValue);

      if (progressValue >= 1) {
        goNext();
        return;
      }

      raf = requestAnimationFrame(tick);
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => selectCategory(tab.dataset.category, true));
    });

    reviews.forEach((review) => {
      review.addEventListener('click', () => {
        if (review.dataset.category !== activeCategory) {
          selectCategory(review.dataset.category, false);
        }
        selectReview(review, true);
      });
    });

    voices.forEach((voice) => {
      voice.addEventListener('click', () => {
        const review = reviews.find((item) => item.dataset.reviewId === voice.dataset.reviewId);
        if (!review) return;

        const targetCategory = voice.dataset.category || review.dataset.category;
        if (targetCategory !== activeCategory) {
          selectCategory(targetCategory, false);
        }

        selectReview(review, true);
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === root) {
            inView = entry.isIntersecting && entry.intersectionRatio > 0.12;
          }
        });
      }, { threshold: [0, 0.12, 0.35] });

      observer.observe(root);
      root._hkwttObserver = observer;
    }

    let initial = selectCategory(activeCategory, true);
    if (!initial.length) {
      const firstAvailableTab = tabs.find((tab) =>
        reviews.some((review) => review.dataset.category === tab.dataset.category)
      );
      if (firstAvailableTab) {
        selectCategory(firstAvailableTab.dataset.category, true);
      }
    }

    root._hkwttDestroy = () => {
      destroyed = true;
      cancelAnimationFrame(raf);
      root._hkwttObserver?.disconnect?.();
      clearPlayer();
    };
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
  };

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target?.querySelector?.(ROOT_SELECTOR);
    root?._hkwttDestroy?.();
  });

  initAll();
})();
